import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, Divider, LinearProgress, Paper, TextField, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';
import {
  fetchAllFilteredTimeEvents,
  fetchValidationLocks,
  fetchValidationProjectSummaries,
  fetchTimeeventById,
  updateTimeeventValidation,
} from '../Utils/timeEventsApi';
import { useUser, useWebSocket } from './WebSocketContext';
import TimeEventTag from './Timevents/TimeEventTag';
import PDFViewer from './Viewer/PDFViewer';
import ImageViewer from './Viewer/ImageViewer';

const viewerTypeFor = (eventsource = '') => {
  const rawSource = String(eventsource).toLowerCase();
  // Ingestion may persist the internal .dataunfold suffix. The timeline
  // removes it before selecting a viewer; validation must do the same so PDF
  // and image viewers receive the event and can render coordinate overlays.
  const source = rawSource.endsWith('.dataunfold')
    ? rawSource.slice(0, -'.dataunfold'.length)
    : rawSource;
  if (source.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp|tiff?)$/.test(source)) return 'image';
  if (/\.(docx?|odt)$/.test(source)) return 'word';
  if (/\.(xlsx?|csv)$/.test(source)) return source.endsWith('.csv') ? 'csv' : 'excel';
  if (/\.(txt|md|log)$/.test(source)) return 'text';
  if (/\.(mp3|wav|m4a|ogg|flac|aac)$/.test(source)) return 'audio';
  return 'other';
};

const VALIDATION_QUEUE_PAGE_SIZE = 1000;

export default function ValidatorWorkspace() {
  const { caseid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isValidator, username } = useUser();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const [projects, setProjects] = useState([]);
  const [projectSummaries, setProjectSummaries] = useState([]);
  const [events, setEvents] = useState([]);
  const [index, setIndex] = useState(0);
  const [event, setEvent] = useState(null);
  const [metadata, setMetadata] = useState('');
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [totalPending, setTotalPending] = useState(0);
  const [validationLock, setValidationLock] = useState({ eventId: null, token: '', status: 'idle' });
  const [validationLocks, setValidationLocks] = useState([]);
  const lastLockHeartbeatRef = useRef(0);
  const reviewedEventCount = 0;

  const currentSummary = events[index] || null;

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const [payload, summaries] = await Promise.all([
        apiRequest('/api/v1/projects?limit=200'),
        fetchValidationProjectSummaries(),
      ]);
      setProjects(payload?.projects || payload?.cases || payload?.data?.projects || payload?.data?.cases || []);
      setProjectSummaries(summaries);
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.load_projects_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadEvents = useCallback(async () => {
    if (!caseid) return;
    setLoading(true);
    setError('');
    try {
      const allEvents = await fetchAllFilteredTimeEvents(caseid, {
        pageSize: VALIDATION_QUEUE_PAGE_SIZE,
        markAsRead: false,
      });
      const unconfirmed = allEvents.filter((item) => Number(item?.isConfirmed) !== 1);
      setEvents(unconfirmed);
      setTotalPending(unconfirmed.length);
      setIndex(0);
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.load_timeevents_error'));
    } finally {
      setLoading(false);
    }
  }, [caseid, t]);

  useEffect(() => {
    if (!isValidator) return;
    if (caseid) loadEvents();
    else loadProjects();
  }, [caseid, isValidator, loadEvents, loadProjects]);

  useEffect(() => {
    if (!caseid || !isValidator) return undefined;
    const refreshLocks = () => fetchValidationLocks(caseid).then(setValidationLocks).catch(() => {});
    refreshLocks();
    const timer = window.setInterval(refreshLocks, 15000);
    return () => window.clearInterval(timer);
  }, [caseid, isValidator]);

  useEffect(() => {
    if (!currentSummary) {
      setEvent(null);
      return;
    }
    let active = true;
    setLoading(true);
    fetchTimeeventById(currentSummary.id)
      .then((loadedEvent) => {
        if (!active) return;
        setEvent(loadedEvent);
        setMetadata(loadedEvent?.eventdata == null ? '' : String(loadedEvent.eventdata));
        setTags(Array.isArray(loadedEvent?.customFields) ? loadedEvent.customFields : []);
      })
      .catch((requestError) => active && setError(requestError?.message || t('validation_workspace.load_timeevent_error')))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [currentSummary, t]);

  useEffect(() => subscribeToMessages(['validation_lock', 'validation_lock_heartbeat'], (message) => {
    const data = message?.data || {};
    if (message.command === 'validation_lock' && Number(data.event_id) === Number(currentSummary?.id)) {
      lastLockHeartbeatRef.current = data.acquired ? Date.now() : 0;
      setValidationLock({
        eventId: Number(data.event_id),
        token: data.acquired ? String(data.lock_token || '') : '',
        status: data.acquired ? 'held' : 'unavailable',
      });
      if (!data.acquired) {
        setEvents((previous) => previous.filter((item) => Number(item.id) !== Number(data.event_id)));
        setIndex((previous) => Math.min(previous, Math.max(0, events.length - 2)));
      }
    }
    if (message.command === 'validation_lock_heartbeat' && Number(data.event_id) === Number(validationLock.eventId)) {
      if (data.renewed) {
        lastLockHeartbeatRef.current = Date.now();
      } else {
        lastLockHeartbeatRef.current = 0;
        setValidationLock((previous) => ({ ...previous, token: '', status: 'expired' }));
      }
    }
  }), [currentSummary?.id, events.length, subscribeToMessages, validationLock.eventId]);

  useEffect(() => {
    if (!currentSummary?.id || !isValidator) return;
    lastLockHeartbeatRef.current = 0;
    setValidationLock({ eventId: Number(currentSummary.id), token: '', status: 'acquiring' });
    sendMessage({ command: 'validation_lock_acquire', event_id: Number(currentSummary.id) });
  }, [currentSummary?.id, isValidator, sendMessage]);

  useEffect(() => {
    if (validationLock.status !== 'held' || !validationLock.token) return undefined;
    const timer = window.setInterval(() => {
      sendMessage({ command: 'validation_lock_heartbeat', event_id: validationLock.eventId, lock_token: validationLock.token });
    }, 30000);
    return () => window.clearInterval(timer);
  }, [sendMessage, validationLock]);

  useEffect(() => {
    if (validationLock.status !== 'held') return undefined;
    const timer = window.setInterval(() => {
      const heartbeatIsMissing = Date.now() - lastLockHeartbeatRef.current > 45000;
      if (document.visibilityState === 'visible' && heartbeatIsMissing) {
        setValidationLock((previous) => ({ ...previous, token: '', status: 'expired' }));
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [validationLock.status]);

  useEffect(() => () => {
    if (validationLock.token && validationLock.eventId) {
      sendMessage({ command: 'validation_lock_release', event_id: validationLock.eventId, lock_token: validationLock.token });
    }
  }, [sendMessage, validationLock.eventId, validationLock.token]);

  const save = useCallback(async () => {
    if (!event) return false;
    setSaving(true);
    setError('');
    try {
      if (metadata !== String(event.eventdata ?? '')) {
        await updateTimeeventValidation(event.id, { metadata }, { lockToken: validationLock.token });
      }
      // TimeEventTag persists only the field the validator actually edits.
      // Do not resend the full tag snapshot here: it creates duplicate audit
      // records and can overwrite a newer value with stale workspace state.
      setEvent((previous) => ({ ...previous, eventdata: metadata, customFields: tags }));
      return true;
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.save_error'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [event, metadata, t, tags, validationLock.token]);

  const confirmAndNext = useCallback(async () => {
    if (!await save()) return;
    setSaving(true);
    try {
      await updateTimeeventValidation(event.id, { confirm: true }, { lockToken: validationLock.token });
      setEvents((previous) => previous.filter((item) => item.id !== event.id));
      setIndex((previous) => Math.min(previous, Math.max(0, events.length - 2)));
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.confirm_error'));
    } finally {
      setSaving(false);
    }
  }, [event, events.length, save, t, validationLock.token]);

  useEffect(() => {
    const onKeyDown = (keyboardEvent) => {
      const targetTag = String(keyboardEvent.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(targetTag) || keyboardEvent.metaKey || keyboardEvent.ctrlKey || keyboardEvent.altKey) return;
      if (keyboardEvent.key === 'ArrowLeft' && index > 0) setIndex((current) => current - 1);
      if (keyboardEvent.key === 'ArrowRight' && index < events.length - 1) setIndex((current) => current + 1);
      if (keyboardEvent.key.toLowerCase() === 'c') confirmAndNext();
      if (keyboardEvent.key.toLowerCase() === 's') save();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmAndNext, events.length, index, save]);

  const progress = useMemo(() => `${Math.max(totalPending - events.length - reviewedEventCount, 0)} confirmés · ${reviewedEventCount} à revoir · ${events.length} restants`, [events.length, reviewedEventCount, totalPending]);
  const validationProgress = useMemo(() => {
    const processedCount = Math.max(totalPending - events.length, 0);
    return {
      processedCount,
      value: totalPending ? (processedCount / totalPending) * 100 : 0,
    };
  }, [events.length, totalPending]);
  const validationLocksForDisplay = useMemo(() => {
    const locksByEventId = new Map(validationLocks.map((lock) => [Number(lock.event_id), lock]));
    if (validationLock.status === 'held' && validationLock.eventId && !locksByEventId.has(Number(validationLock.eventId))) {
      locksByEventId.set(Number(validationLock.eventId), {
        event_id: validationLock.eventId,
        username,
      });
    }
    return Array.from(locksByEventId.values());
  }, [username, validationLock.eventId, validationLock.status, validationLocks]);
  const projectSummaryById = useMemo(() => new Map(
    projectSummaries.map((summary) => [Number(summary.case_id), summary])
  ), [projectSummaries]);
  const totalProjectsPending = useMemo(() => projectSummaries.reduce(
    (total, summary) => total + Number(summary.pending_count || 0), 0
  ), [projectSummaries]);

  const updateTagInWorkspace = useCallback((updatedTag) => {
    setTags((previous) => previous.map((tag) => tag.id === updatedTag.id ? updatedTag : tag));
    setEvent((previous) => previous ? {
      ...previous,
      customFields: (previous.customFields || []).map((tag) => tag.id === updatedTag.id ? updatedTag : tag),
    } : previous);
  }, []);
  const removeTagFromWorkspace = useCallback((deletedTag) => {
    setTags((previous) => previous.filter((tag) => tag.id !== deletedTag.id));
    setEvent((previous) => previous ? {
      ...previous,
      customFields: (previous.customFields || []).filter((tag) => tag.id !== deletedTag.id),
    } : previous);
  }, []);
  if (!isValidator) return <Typography sx={{ p: 4 }}>{t('validation_workspace.access_denied')}</Typography>;
  if (!caseid) return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1100, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, background: 'linear-gradient(135deg, #5757b0, #7777d0)', color: 'primary.contrastText' }}>
        <Typography variant="h5" fontWeight="bold">{t('validation_workspace.inbox_title')}</Typography>
        <Typography sx={{ mt: 0.5, opacity: 0.9 }}>{t('validation_workspace.inbox_subtitle')}</Typography>
        <Typography variant="h3" fontWeight="bold" sx={{ mt: 2 }}>{totalProjectsPending}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>{t('validation_workspace.pending_total')}</Typography>
      </Paper>
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : totalProjectsPending === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 44 }} />
          <Typography variant="h6" sx={{ mt: 1 }}>{t('validation_workspace.inbox_empty_title')}</Typography>
          <Typography color="text.secondary">{t('validation_workspace.inbox_empty_description')}</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 2 }}>
          {projects.map((project) => {
            const summary = projectSummaryById.get(Number(project.id)) || {};
            const pending = Number(summary.pending_count || 0);
            const total = Number(summary.total_count || 0);
            const locked = Number(summary.locked_count || 0);
            if (!pending) return null;
            const progressValue = total ? ((total - pending) / total) * 100 : 0;
            return <Paper key={project.id} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="bold" noWrap>{project.title || project.name || t('validation_workspace.project_fallback', { id: project.id })}</Typography>
              <Typography variant="h4" color="primary.main" fontWeight="bold">{pending}</Typography>
              <Typography variant="body2" color="text.secondary">{t('validation_workspace.pending_in_project')}</Typography>
              <LinearProgress variant="determinate" value={progressValue} sx={{ height: 7, borderRadius: 4 }} />
              {locked > 0 && <Typography variant="caption" color="text.secondary">{t('validation_workspace.being_validated', { count: locked })}</Typography>}
              <Button variant="contained" onClick={() => navigate(`/validate/${project.id}`)} sx={{ mt: 'auto' }}>{t('validation_workspace.start_validation')}</Button>
            </Paper>;
          })}
        </Box>
      )}
    </Box>
  );
  if (!loading && !event) return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h6">{t('validation_workspace.complete_title')}</Typography>
        <Typography sx={{ mt: 1 }}>
          {t('validation_workspace.complete_summary', { count: totalPending })}
        </Typography>
      </Paper>
    </Box>
  );

  return <Box sx={{ p: 1, height: '100dvh', boxSizing: 'border-box', overflow: 'hidden', background: '#f7f8fc' }}>
    <Paper sx={{ px: 1.25, py: 0.75, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1, minWidth: 180, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>{t('validation_workspace.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexGrow: 1, maxWidth: 240 }}>
          <LinearProgress
            variant="determinate"
            value={validationProgress.value}
            aria-label="Progression de validation"
            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {validationProgress.processedCount} / {totalPending}
          </Typography>
        </Box>
        {validationLocksForDisplay.length > 0 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {t('validation_workspace.in_progress')}:
          </Typography>
          {validationLocksForDisplay.map((lock) => {
            const isCurrentUser = String(lock.username || '').toLowerCase() === String(username || '').toLowerCase();
            return (
            <Box
              key={lock.event_id}
              title={lock.username}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.65, py: 0.2, borderRadius: 1,
                bgcolor: isCurrentUser ? 'primary.main' : 'grey.100',
                color: isCurrentUser ? 'primary.contrastText' : 'text.secondary',
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 15, color: 'inherit' }} aria-label={lock.username} />
              <Typography variant="caption" sx={{ color: 'inherit', fontWeight: isCurrentUser ? 700 : 500 }}>
                {isCurrentUser ? t('validation_workspace.you') : lock.username} · #{lock.event_id}
              </Typography>
            </Box>
            );
          })}
        </Box>}
      </Box>
      {/*
      <Typography variant="h6" sx={{ flexGrow: 1 }}>Validation — {progress} restant(s)</Typography>
      <Button startIcon={<NavigateBeforeIcon />} disabled={index === 0 || saving} onClick={() => setIndex(index - 1)}>{t('validation_workspace.previous')}</Button>
      */}
      {/*
      <Button startIcon={<NavigateBeforeIcon />} disabled={index === 0 || saving} onClick={() => setIndex(index - 1)}>Précédent</Button>
      */}
      <Button startIcon={<NavigateBeforeIcon />} disabled={index === 0 || saving} onClick={() => setIndex(index - 1)}>{t('validation_workspace.previous')}</Button>
      <Button endIcon={<NavigateNextIcon />} disabled={index >= events.length - 1 || saving} onClick={() => setIndex(index + 1)}>{t('validation_workspace.next')}</Button>
    </Paper>
    {validationLock.status === 'expired' && (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>{t('validation_workspace.reload')}</Button>}
      >
        {t('validation_workspace.lock_expired')}
      </Alert>
    )}
    {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
    {loading ? <CircularProgress /> : !event ? <Paper sx={{ p: 4 }}><Typography variant="h6">File de validation terminée</Typography><Typography sx={{ mt: 1 }}>{Math.max(totalPending - reviewedEventCount, 0)} confirmés · {reviewedEventCount} à revoir.</Typography></Paper> :
      <Box sx={{ display: 'grid', height: 'calc(100dvh - 62px)', minHeight: 0, gridTemplateColumns: { xs: '1fr', lg: 'minmax(560px, 65%) minmax(320px, 35%)' }, gap: 1 }}>
        <Paper sx={{ minHeight: 0, height: '100%', overflow: 'hidden', p: 0 }}>
          {viewerTypeFor(event.eventsource) === 'pdf' ? (
            <PDFViewer
              open
              embedded
              onClose={() => {}}
              filename={String(event.eventsource || '').split(/[\\/]/).pop()}
              eventId={event.id}
              filePath={event.eventsource}
              timeevent={event}
            />
          ) : viewerTypeFor(event.eventsource) === 'image' ? (
            <ImageViewer
              open
              embedded
              onClose={() => {}}
              filename={String(event.eventsource || '').split(/[\\/]/).pop()}
              eventId={event.id}
              filePath={event.eventsource}
              timeevent={event}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold">{event.eventsource}</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>{t('validation_workspace.viewer_unavailable')}</Typography>
            </Box>
          )}
        </Paper>
        <Paper sx={{ minHeight: 0, overflow: 'auto', p: 1.5 }}>
          <Typography variant="h6" fontWeight="bold">{t('validation_workspace.tags_title')}</Typography>
          {validationLock.status !== 'held' && <Typography variant="caption" color="warning.main">
            {validationLock.status === 'unavailable'
              ? `En cours de validation par ${validationLocks.find((lock) => Number(lock.event_id) === Number(event?.id))?.username || 'un autre utilisateur'}.`
              : 'Verrouillage de validation en cours…'}
          </Typography>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('validation_workspace.tags_help')}
          </Typography>
          <Box sx={{ minHeight: 360, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
            {tags.map((tag, tagIndex) => (
              <Box key={tag.id || tagIndex} sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeEventTag tag={tag} timeevent={event} canManageProject={validationLock.status === 'held'} showFullValue validationLockToken={validationLock.token} onUpdate={updateTagInWorkspace} onDelete={removeTagFromWorkspace} />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button startIcon={<SaveIcon />} disabled={saving || validationLock.status !== 'held'} onClick={save}>{t('validation_workspace.save')}</Button>
            {/*
            <Button disabled={saving} onClick={() => setReviewDialogOpen(true)}>À revoir</Button>
            */}
            <Button variant="contained" startIcon={<CheckCircleIcon />} disabled={saving || validationLock.status !== 'held'} onClick={confirmAndNext}>{t('validation_workspace.confirm_next')}</Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">{t('validation_workspace.information')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField label={t('validation_workspace.metadata')} value={metadata} onChange={(e) => setMetadata(e.target.value)} multiline minRows={5} fullWidth />
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Box>}
    {/*
    <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Mettre à revoir</DialogTitle>
      <DialogContent><TextField autoFocus label="Commentaire (optionnel)" value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} multiline minRows={3} fullWidth sx={{ mt: 1 }} /></DialogContent>
      <DialogActions><Button onClick={() => setReviewDialogOpen(false)}>Annuler</Button><Button variant="contained" onClick={markForReview}>Mettre à revoir</Button></DialogActions>
    </Dialog>
    */}
  </Box>;
}
